/**
 * Local-timezone date formatting helpers.
 *
 * All timestamps in WingWise are stored in UTC (backend emits ISO 8601 with
 * `+00:00`). The UI should ALWAYS render absolute dates in the viewer's
 * browser-local timezone — never string-slice `iso.slice(0, 10)` (that
 * bakes in the UTC date and produces off-by-one errors near midnight for
 * users west of UTC).
 *
 * Use these helpers everywhere:
 *   formatLocalDate(iso)     → "8/6/2026"      (locale-formatted)
 *   formatLocalDateTime(iso) → "8/6/2026, 10:20 PM"
 *   formatLocalDateISO(iso)  → "2026-08-06"    (stable ISO-shaped, local tz)
 *
 * All three are null-safe — return "" for falsy input, "" if the ISO can't
 * be parsed.
 */

function _parse(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/** Locale-formatted local date. Example: "8/6/2026" (en-US) / "06/08/2026" (en-GB). */
export function formatLocalDate(iso, opts) {
  const d = _parse(iso);
  return d ? d.toLocaleDateString(undefined, opts) : "";
}

/** Locale-formatted local date + time. Example: "8/6/2026, 10:20 PM". */
export function formatLocalDateTime(iso, opts) {
  const d = _parse(iso);
  return d
    ? d.toLocaleString(undefined, opts || {
      year: "numeric", month: "numeric", day: "numeric",
      hour: "numeric", minute: "2-digit",
    })
    : "";
}

/**
 * "YYYY-MM-DD" in the browser's LOCAL timezone. Use when you need a
 * stable ISO-shaped label that still respects the viewer's clock (e.g.
 * chart tick labels, sortable date cells in tables).
 *
 * Never do `iso.slice(0, 10)` — that gives you the UTC date and users
 * west of UTC will see tomorrow's date after ~8 PM.
 */
export function formatLocalDateISO(iso) {
  const d = _parse(iso);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
